import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';

import { calendarDay, pointTime, spanText } from '../../shared/lib/format';
import { Button } from '../../shared/ui/button';
import { CheckIcon, ClockIcon, CredentialIcon, DeleteIcon, StopIcon } from '../../shared/ui/icons';
import { MenuItem } from '../../shared/ui/menu';
import { Note } from '../../shared/ui/note';
import { RowMenu } from '../../shared/ui/row-menu';
import { PanelFoot, SectionPanel } from '../../shared/ui/section-panel';
import { StatusDisc } from '../../shared/ui/status-disc';
import {
  liveSecrets,
  sourceClientKeys,
  upsertCustomerSecret,
  type IClientSource,
  type TCustomerSecret,
  type TSourceCustomer,
} from '../../entities/customer';
import { credentialedMethods, methodName, sourceAuthMethods } from '../../entities/source';
import { EndOldDialog } from './end-old-dialog';
import { RevokeDialog } from './revoke-dialog';
import { SecretDialog } from './secret-dialog';
import { SecretShownOnce } from './secret-shown-once';
import { SecretLine } from './ui/secret-line';

const DAY_MS = 86_400_000;
const ENDED_SHOWN_MS = 7 * DAY_MS;

// The last one to stop, while it is recent enough that "why did they stop verifying" is a question.
const recentlyEnded = (secrets: readonly TCustomerSecret[], now: number) =>
  secrets
    .filter(
      (secret) => (secret.state === 'expired' || secret.state === 'revoked') && secret.expiresAt,
    )
    .filter((secret) => now - new Date(secret.expiresAt ?? '').getTime() < ENDED_SHOWN_MS)
    .sort((a, b) => (b.expiresAt ?? '').localeCompare(a.expiresAt ?? ''))[0] ?? null;

const matchedText = (secret: TCustomerSecret, now: number, timeZone?: string): string =>
  secret.lastUsedAt
    ? `last matched ${pointTime(secret.lastUsedAt, now, timeZone)}`
    : 'not matched yet';

const endedNote = (secret: TCustomerSecret): string => {
  const how = secret.state === 'expired' ? 'Ended on time.' : 'Ended early.';
  if (!secret.lastUsedAt || !secret.expiresAt) return `${how} It never matched a request.`;
  const quiet = new Date(secret.expiresAt).getTime() - new Date(secret.lastUsedAt).getTime();
  return quiet > 0 ? `${how} It hadn’t matched for ${spanText(quiet)} before it ended.` : how;
};

interface IDialog {
  mode: 'rotate' | 'add';
  origin: 'generate' | 'paste';
  methodId?: string;
}

interface IClientSecretCardProps {
  sourceId: string;
  source: IClientSource;
  row: TSourceCustomer;
  now: number;
  timeZone?: string;
}

const SetUpSigning = ({ sourceId, sourceName }: { sourceId: string; sourceName: string }) => {
  const navigate = useNavigate();
  return (
    <Button
      size="sm"
      variant="secondary"
      className="mt-1"
      onClick={() =>
        void navigate({ to: '/sources/$sourceId', params: { sourceId }, hash: 'signing' })
      }
    >
      <CredentialIcon size={12} /> Set up signing on {sourceName}
    </Button>
  );
};

export const ClientSecretCard = ({
  sourceId,
  source,
  row,
  now,
  timeZone,
}: IClientSecretCardProps) => {
  const client = useQueryClient();
  const name = row.customer.name;
  const [dialog, setDialog] = useState<IDialog | null>(null);
  const [ending, setEnding] = useState<TCustomerSecret | null>(null);
  const [revoking, setRevoking] = useState<TCustomerSecret | null>(null);
  const [shown, setShown] = useState<string | null>(null);

  const methods = useQuery({
    queryKey: ['source-auth-methods', sourceId],
    queryFn: () => sourceAuthMethods(sourceId),
    enabled: source.secrets,
  });
  const credentialed = credentialedMethods(methods.data ?? []);
  const several = credentialed.length > 1;

  // The source's own query carries the "without a working key" count this changes.
  const refresh = () => {
    void client.invalidateQueries({ queryKey: sourceClientKeys.all(sourceId) });
    void client.invalidateQueries({ queryKey: ['source', sourceId] });
  };

  const generate = useMutation({
    mutationFn: (authMethodId: string | undefined) =>
      upsertCustomerSecret(row.customer.id, sourceId, authMethodId ? { authMethodId } : {}),
    onSuccess: (result) => {
      refresh();
      setShown(result.secret.plaintext);
    },
  });

  if (!source.checked) {
    return (
      <SectionPanel title="Their secret" note="none · source checks nothing">
        <div className="p-card">
          <div className="flex flex-col items-start gap-2 rounded-control border border-dashed border-border-strong px-card py-4">
            <p className="text-data text-primary">
              {source.name} checks nothing yet, so {name} has no secret.
            </p>
            <p className="text-meta text-muted">
              Once {source.name} checks signatures, each client gets their own secret here, and
              requests without it are turned away.
            </p>
            <SetUpSigning sourceId={sourceId} sourceName={source.name} />
          </div>
        </div>
      </SectionPanel>
    );
  }

  if (!source.secrets) {
    return (
      <SectionPanel title="Their secret" note="none · checked by IP allowlist">
        <div className="p-card">
          <div className="flex flex-col items-start gap-2 rounded-control border border-dashed border-border-strong px-card py-4">
            <p className="text-data text-primary">
              {source.name} checks only the IP address requests come from, so {name} has no secret.
            </p>
            <p className="text-meta text-muted">
              The allowlist is the same for every client. Once {source.name} also checks a signature
              or an API key, each client gets their own secret here.
            </p>
            <SetUpSigning sourceId={sourceId} sourceName={source.name} />
          </div>
        </div>
      </SectionPanel>
    );
  }

  const live = liveSecrets(row.secrets);
  const current = live.current;
  const old = live.old;
  const ended = old.length === 0 ? recentlyEnded(row.secrets, now) : null;
  const liveCount = current.length + old.length;
  const nameOf = (secret: TCustomerSecret): string | null => {
    const method = methods.data?.find((m) => m.id === secret.authMethodId);
    return method ? methodName(method) : null;
  };
  // With one method the panel's note names it; with several each line says which it is for.
  const withMethod = (secret: TCustomerSecret, facts: string): string => {
    const method = several ? nameOf(secret) : null;
    return method ? `${method} · ${facts}` : facts;
  };
  const scheme = several
    ? `${credentialed.length} methods`
    : credentialed[0]
      ? methodName(credentialed[0])
      : undefined;
  const oldStillUsed = old.some(
    (secret) => secret.lastUsedAt != null && now - new Date(secret.lastUsedAt).getTime() < DAY_MS,
  );
  const openAdd = (origin: IDialog['origin']) => setDialog({ mode: 'add', origin });

  const dialogs = (
    <>
      {dialog && (
        <SecretDialog
          open
          onOpenChange={(open) => !open && setDialog(null)}
          mode={dialog.mode}
          initialOrigin={dialog.origin}
          customerId={row.customer.id}
          sourceId={sourceId}
          clientName={name}
          methods={credentialed}
          initialMethodId={dialog.methodId}
          onDone={(result, generated) => {
            setDialog(null);
            refresh();
            if (generated) setShown(result.secret.plaintext);
          }}
        />
      )}
      <EndOldDialog
        old={ending}
        live={liveCount}
        sourceId={sourceId}
        clientName={name}
        now={now}
        timeZone={timeZone}
        onClose={() => setEnding(null)}
        onEnded={() => {
          setEnding(null);
          refresh();
        }}
      />
      <RevokeDialog
        secret={revoking}
        others={liveCount - 1}
        sourceId={sourceId}
        source={source}
        clientName={name}
        now={now}
        timeZone={timeZone}
        onClose={() => setRevoking(null)}
        onRevoked={() => {
          setRevoking(null);
          refresh();
        }}
      />
      <SecretShownOnce plaintext={shown} clientName={name} onClose={() => setShown(null)} />
    </>
  );

  const endedLine = ended && (
    <SecretLine
      label="Old"
      hint={ended.secretHint}
      facts={withMethod(
        ended,
        `ended ${pointTime(ended.expiresAt ?? '', now, timeZone)} · ${matchedText(ended, now, timeZone)}`,
      )}
      state={
        <>
          <StatusDisc tone="suppressed" icon={StopIcon} /> Ended
        </>
      }
    />
  );

  if (liveCount === 0) {
    return (
      <SectionPanel
        title="Their secret"
        note={row.secrets.length === 0 ? 'none yet' : 'none working'}
      >
        <div className="p-card">
          <div className="flex flex-col items-center gap-2 rounded-control border border-dashed border-border-strong px-card py-4 text-center">
            <p className="text-data text-primary">Add the secret {name} sign with</p>
            <p className="max-w-prose-line text-meta text-muted">
              {source.rejects
                ? 'Until then nothing sent to their address can be checked, so it is all rejected and no run starts.'
                : `Until then nothing sent to their address can be checked, so ${source.name} delivers it marked as failed.`}
            </p>
            <div className="mt-1 flex flex-wrap justify-center gap-2">
              <Button size="sm" onClick={() => openAdd('paste')}>
                Paste their secret
              </Button>
              {/* With a choice of method the dialog asks which; generating blind would pick one. */}
              <Button
                size="sm"
                variant="secondary"
                disabled={methods.isPending}
                waiting={generate.isPending}
                waitingLabel="Generating"
                onClick={() =>
                  several ? openAdd('generate') : generate.mutate(credentialed[0]?.id)
                }
              >
                Generate one for them{several ? '…' : ''}
              </Button>
            </div>
            {generate.isError && <Note tone="danger">Not generated: {generate.error.message}</Note>}
          </div>
        </div>
        {endedLine}
        {dialogs}
      </SectionPanel>
    );
  }

  const lastOld = old.at(-1) ?? null;
  const oldWord = old.length === 1 ? 'the old secret' : 'an old secret';

  return (
    <SectionPanel
      title="Their secret"
      note={scheme}
      actions={
        current.length === 0 ? (
          <Button size="sm" variant="secondary" onClick={() => openAdd('paste')}>
            <CredentialIcon size={12} /> Add a new secret…
          </Button>
        ) : old.length === 1 ? (
          <Button size="sm" variant="ghost" onClick={() => setEnding(old[0])}>
            End the old one now…
          </Button>
        ) : old.length === 0 ? (
          <Button
            size="sm"
            variant="secondary"
            onClick={() =>
              setDialog({ mode: 'rotate', origin: 'generate', methodId: current[0].authMethodId })
            }
          >
            <CredentialIcon size={12} /> Rotate…
          </Button>
        ) : undefined
      }
    >
      {current.map((secret) => (
        <SecretLine
          key={secret.secretId}
          label={old.length > 0 ? 'New' : 'Current'}
          hint={secret.secretHint}
          facts={withMethod(
            secret,
            `since ${old.length > 0 ? pointTime(secret.createdAt, now, timeZone) : calendarDay(secret.createdAt, now, timeZone)} · ${matchedText(secret, now, timeZone)}`,
          )}
          state={
            <>
              <StatusDisc tone="success" icon={CheckIcon} /> In use
            </>
          }
          menu={
            <RowMenu label="This secret">
              <MenuItem tone="danger" onSelect={() => setRevoking(secret)}>
                <DeleteIcon /> Revoke…
              </MenuItem>
            </RowMenu>
          }
        />
      ))}
      {old.map((secret) => (
        <SecretLine
          key={secret.secretId}
          label="Old"
          hint={secret.secretHint}
          facts={withMethod(
            secret,
            `${secret.expiresAt ? `ends ${pointTime(secret.expiresAt, now, timeZone)} · ` : ''}${matchedText(secret, now, timeZone)}`,
          )}
          state={
            <>
              <StatusDisc tone="motion" icon={ClockIcon} />
              {secret.expiresAt
                ? `Ending in ${spanText(Math.max(0, new Date(secret.expiresAt).getTime() - now))}`
                : 'Ending'}
            </>
          }
          menu={
            <RowMenu label="This old secret">
              <MenuItem tone="danger" onSelect={() => setEnding(secret)}>
                <StopIcon /> End it now…
              </MenuItem>
            </RowMenu>
          }
        />
      ))}
      {endedLine}
      <PanelFoot>
        {lastOld && current.length === 0
          ? `Nothing replaces ${old.length === 1 ? 'it' : 'them'}: once ${old.length === 1 ? 'it ends' : 'the last one ends'}, ${source.rejects ? `everything ${name} sends is rejected` : `what ${name} sends is delivered marked as failed`}. Add their new secret before then.`
          : lastOld
            ? oldStillUsed
              ? `${name} still signs some requests with ${oldWord}. Once it hasn’t matched for a day, ending it early is safe.`
              : `${old.length === 1 ? 'The old secret hasn’t' : 'No old secret has'} matched for a day, so ending ${old.length === 1 ? 'it' : 'them'} early is safe.`
            : ended
              ? endedNote(ended)
              : `Rotate when ${name} changes theirs. The old one can keep working for a while, so nothing is rejected in between.`}
      </PanelFoot>
      {dialogs}
    </SectionPanel>
  );
};
